<template>
  <v-card outlined class="mb-4">
    <v-card-title class="refund-section-title">{{ $t('refund.transactions') }}</v-card-title>
    <v-divider></v-divider>

    <div v-if="!transactions.length" class="refund-empty">{{ $t('refund.noTransactions') }}</div>

    <v-simple-table v-else dense>
      <thead>
        <tr>
          <th scope="col">{{ $t('refund.transactionDate') }}</th>
          <th scope="col">{{ $t('refund.pspCode') }}</th>
          <th scope="col">{{ $t('refund.pspResponse') }}</th>
          <th scope="col">{{ $t('refund.status') }}</th>
          <th scope="col">{{ $t('refund.authorizationCode') }}</th>
          <th scope="col" class="text-right">{{ $t('refund.amountRefundedShort') }}</th>
          <th scope="col" class="text-right">{{ $t('refund.amountRemaining') }}</th>
          <th scope="col">{{ $t('refund.operationDate') }}</th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(transaction, index) in transactions" :key="transaction.transaction_id || `tx-${index}`">
          <td>{{ formatDate(transaction.created_at) }}</td>
          <td>
            <span v-if="transaction.is_manual">
              <v-chip x-small outlined color="grey">{{ $t('refund.manual') }}</v-chip>
            </span>
            <span v-else>{{ displayOrDash(transaction.psp_code) }}</span>
          </td>
          <td
            :class="{
              'refund-muted': transaction.current_status === 'failure' || transaction.current_status === 'error',
            }"
          >
            {{ displayOrDash(transaction.psp_response) }}
          </td>
          <td>
            <v-chip
              v-if="transaction.current_status && hasStatusHistory(transaction)"
              x-small
              :color="statusBackgroundColor(transaction.current_status)"
              :text-color="statusTextColor(transaction.current_status)"
              @click="openHistoryDialog(transaction)"
            >
              {{ statusLabel(transaction.current_status) }}
              <v-icon :color="statusTextColor(transaction.current_status)" x-small right>mdi-history</v-icon>
            </v-chip>
            <v-chip
              v-else-if="transaction.current_status"
              x-small
              :color="statusBackgroundColor(transaction.current_status)"
              :text-color="statusTextColor(transaction.current_status)"
            >
              {{ statusLabel(transaction.current_status) }}
            </v-chip>
            <span v-else>—</span>
          </td>
          <td>{{ displayOrDash(transaction.authorization_code) }}</td>
          <td class="text-right">{{ formatAmount(transaction.refunded_amount) }}</td>
          <td class="text-right">{{ formatAmount(transaction.remaining_amount) }}</td>
          <td>{{ formatDate(transaction.operation_date) }}</td>
          <td class="text-right">
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small :disabled="!hasMenuActions(transaction)" v-bind="attrs" v-on="on">
                  <v-icon :color="hasMenuActions(transaction) ? undefined : 'grey lighten-1'">mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-subheader v-if="transaction.authorization_code" class="refund-menu-subheader">
                  {{ transaction.authorization_code }}
                </v-subheader>
                <v-list-item
                  v-if="canForceSuccess(transaction)"
                  :disabled="updating"
                  @click="forceSuccess(transaction)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="success">mdi-check-circle-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('refund.forceStatusSuccess') }}</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="!transaction.authorization_code" @click="copyAuthorizationCode(transaction)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('refund.copyAuthorizationCode') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <status-history-timeline
      v-model="showHistoryDialog"
      :history="targetHistory"
      :title="$t('refund.transactionStatusHistory')"
      :get-label="getTransactionStatusTranslation"
      :get-bg-color="getTransactionStatusBackgroundColor"
      :get-text-color="getTransactionStatusTextColor"
      @close="closeHistoryDialog"
    />
  </v-card>
</template>

<script>
  import moment from 'moment'
  import { EventBus } from '@/event-bus'
  import {
    getCurrencyCode,
    getTransactionStatusBackgroundColor,
    getTransactionStatusTextColor,
    getTransactionStatusTranslation,
    isAmountDefined,
  } from '../../helpers/refundHelper'
  import StatusHistoryTimeline from '@/components/dialogs/StatusHistoryTimeline.vue'

  export default {
    name: 'RefundTransactions',
    components: {
      StatusHistoryTimeline,
    },
    props: {
      transactions: {
        type: Array,
        default: () => [],
      },
      currency: {
        type: Object,
        default: null,
      },
      // Gates the "force success" action, mirroring RefundDetail.canChangeStatus
      // (permissions.refund_update_roles, with octipas-admin always allowed).
      canUpdate: {
        type: Boolean,
        default: false,
      },
      // Locks the "force success" action while an update is being persisted, so a rapid
      // double-click cannot trigger two concurrent PATCH requests.
      updating: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        showHistoryDialog: false,
        // The status_history of the transaction whose chip was clicked (feeds the shared dialog instance).
        targetHistory: [],
      }
    },
    computed: {
      currencyCode() {
        return getCurrencyCode(this.currency)
      },
    },
    methods: {
      // Exposed to the template so they can be passed as resolver props to StatusHistoryTimeline.
      getTransactionStatusTranslation,
      getTransactionStatusBackgroundColor,
      getTransactionStatusTextColor,
      // A transaction's chip is a history trigger only when it carries a non-empty status_history.
      hasStatusHistory(transaction) {
        return Array.isArray(transaction.status_history) && transaction.status_history.length > 0
      },
      openHistoryDialog(transaction) {
        this.targetHistory = transaction.status_history || []
        this.showHistoryDialog = true
      },
      closeHistoryDialog() {
        this.showHistoryDialog = false
      },
      displayOrDash(value) {
        return value === null || value === undefined || value === '' ? '—' : value
      },
      formatDate(value) {
        return value ? moment(value).format('L LTS') : '—'
      },
      formatAmount(amount) {
        if (!isAmountDefined(amount)) {
          return '—'
        }
        return `${this.$options.filters.formatPrice(amount, this.currency)} ${this.currencyCode}`
      },
      statusBackgroundColor(status) {
        return getTransactionStatusBackgroundColor(status)
      },
      statusTextColor(status) {
        return getTransactionStatusTextColor(status)
      },
      statusLabel(status) {
        return getTransactionStatusTranslation(status)
      },
      // "Force success" is actionable only when the user may update and the transaction is not
      // already successful; "copy" only when there is an authorization code to copy. The items
      // stay listed but disabled/greyed out when not actionable.
      canForceSuccess(transaction) {
        return this.canUpdate && transaction.current_status !== 'success'
      },
      // Whether the row's actions menu has at least one usable action. When it has none, the
      // "…" trigger is disabled and greyed out instead of opening a menu of dead items.
      hasMenuActions(transaction) {
        return this.canForceSuccess(transaction) || Boolean(transaction.authorization_code)
      },
      forceSuccess(transaction) {
        if (!this.canForceSuccess(transaction) || this.updating) {
          return
        }
        this.$emit('forceSuccess', transaction)
      },
      async copyAuthorizationCode(transaction) {
        if (!transaction.authorization_code) {
          return
        }
        try {
          await navigator.clipboard.writeText(transaction.authorization_code)
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('refund.authorizationCodeCopied'),
            color: 'success',
          })
        } catch {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('refund.authorizationCodeCopyError'),
            color: 'error',
          })
        }
      },
    },
  }
</script>

<style scoped>
  .refund-section-title {
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
  }
  .refund-empty {
    padding: 24px 16px;
    text-align: center;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.5);
  }
  th {
    text-transform: uppercase;
    font-size: 0.7rem !important;
    letter-spacing: 0.04em;
  }
  .refund-muted {
    color: rgba(0, 0, 0, 0.45);
    font-style: italic;
  }
  .refund-menu-subheader {
    height: 28px;
    font-size: 0.75rem;
    font-weight: 600;
  }
</style>
